import { backendApiService } from './backendApiService';

export interface PAATree {
  tree_id: number;
  root_keyword: string;
  country: string;
  url_id?: string;
  created_at?: string;
  created_date?: string;
  status?: string;
  processing_status?: string;
  total_questions?: number;
  website_id?: string;
}

export interface PAAQuestion {
  question_id: number;
  tree_id: number;
  question_text: string;
  depth_level: number;
  position_in_level: number;
  answer_text?: string;
  source_url?: string;
  source_title?: string;
}

export interface PAARelationship {
  tree_id: number;
  parent_question_id: number;
  child_question_id: number;
}

export interface PAATreeNode {
  id: string;
  question_id?: number;
  text: string;
  depth_level: number;
  position_in_level: number;
  answer_text?: string;
  source_url?: string;
  source_title?: string;
  children: PAATreeNode[];
  isRoot?: boolean;
}

class PAATreeApiService {
  async getTrees(websiteId?: string): Promise<{ trees: PAATree[], error: string | null }> {
    try {
      const response = await backendApiService.axiosInstance.get('/api/paa-trees', {
        params: websiteId ? { website_id: websiteId } : undefined
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching PAA trees:', error);
      return { 
        trees: [], 
        error: error.response?.data?.error || error.message || 'Failed to fetch PAA trees' 
      };
    }
  }

  async getTreeById(treeId: number): Promise<{ tree: PAATree | null, error: string | null }> {
    try {
      const response = await backendApiService.axiosInstance.get(`/api/paa-trees/${treeId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching PAA tree:', error);
      return { 
        tree: null, 
        error: error.response?.data?.error || error.message || 'Failed to fetch PAA tree' 
      };
    }
  }

  async getTreeStructure(treeId: number): Promise<{ 
    tree: PAATree | null, 
    questions: PAAQuestion[], 
    relationships: PAARelationship[], 
    error: string | null 
  }> {
    try {
      const response = await backendApiService.axiosInstance.get(`/api/paa-trees/${treeId}/structure`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching tree structure:', error);
      return { 
        tree: null, 
        questions: [], 
        relationships: [], 
        error: error.response?.data?.error || error.message || 'Failed to fetch tree structure' 
      };
    }
  }

  async buildTreeStructure(treeId: number): Promise<{ tree: PAATreeNode | null, error: string | null }> {
    try {
      const { tree, questions, relationships, error } = await this.getTreeStructure(treeId);
      
      if (error || !tree) {
        return { tree: null, error: error || 'Tree not found' };
      }

      // Create a map of questions by ID for quick lookup
      const questionMap = new Map<number, PAAQuestion>();
      questions.forEach(q => questionMap.set(q.question_id, q));

      // Create a map of children by parent ID
      const childrenMap = new Map<number, number[]>();
      relationships.forEach(rel => {
        if (!childrenMap.has(rel.parent_question_id)) {
          childrenMap.set(rel.parent_question_id, []);
        }
        childrenMap.get(rel.parent_question_id)!.push(rel.child_question_id);
      });

      // Build tree nodes recursively
      const buildNode = (questionId?: number, isRoot = false): PAATreeNode => {
        if (isRoot) {
          // Root node representing the keyword
          const rootNode: PAATreeNode = {
            id: 'root',
            text: tree.root_keyword,
            depth_level: -1,
            position_in_level: 0,
            children: [],
            isRoot: true
          };

          // Get all depth 0 questions (direct children of root)
          const rootQuestions = questions.filter(q => q.depth_level === 0);
          rootNode.children = rootQuestions.map(q => buildNode(q.question_id));

          return rootNode;
        }

        const question = questionMap.get(questionId!);
        if (!question) {
          throw new Error(`Question ${questionId} not found`);
        }

        const node: PAATreeNode = {
          id: `q-${question.question_id}`,
          question_id: question.question_id,
          text: question.question_text,
          depth_level: question.depth_level,
          position_in_level: question.position_in_level,
          answer_text: question.answer_text,
          source_url: question.source_url,
          source_title: question.source_title,
          children: []
        };

        // Get children
        const childIds = childrenMap.get(question.question_id) || [];
        node.children = childIds.map(childId => buildNode(childId));

        return node;
      };

      const treeStructure = buildNode(undefined, true);
      return { tree: treeStructure, error: null };
    } catch (error: any) {
      console.error('Error building tree structure:', error);
      return { tree: null, error: error.message };
    }
  }

  async searchTrees(query: string): Promise<{ trees: PAATree[], error: string | null }> {
    try {
      const response = await backendApiService.axiosInstance.get(`/api/paa-trees/search/${encodeURIComponent(query)}`);
      return response.data;
    } catch (error: any) {
      console.error('Error searching PAA trees:', error);
      return { 
        trees: [], 
        error: error.response?.data?.error || error.message || 'Failed to search PAA trees' 
      };
    }
  }
}

export const paaTreeApiService = new PAATreeApiService();